/* Admin · Tentative Construction Budget & EMI Estimator settings.
 *
 * Reads/writes the `budget_config` document via:
 *   GET  /api/admin/budget/config
 *   PUT  /api/admin/budget/config
 *
 * Feature flags toggleable instantly without a deploy:
 *   - enable_budget_estimator
 *   - enable_emi_estimator
 *   - show_budget_before_unlock / show_detailed_budget_after_unlock
 *   - show_emi_before_unlock    / show_detailed_emi_after_unlock
 *
 * Stage breakdown uses sliders that auto-normalise to 100% on save.
 */
import React, { useEffect, useState } from "react";
import {
  IndianRupee,
  Wallet,
  ToggleLeft,
  ToggleRight,
  Save,
  Loader2,
  Info,
  RotateCcw,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { adminGet, adminPut } from "@/lib/adminApi";

const FLAG_FIELDS = [
  {
    key: "enable_budget_estimator",
    label: "Enable Budget Estimator",
    hint: "Master switch for all construction-cost estimates.",
  },
  {
    key: "enable_emi_estimator",
    label: "Enable EMI Estimator",
    hint: "Master switch for home-loan EMI calculations.",
  },
  {
    key: "show_budget_before_unlock",
    label: "Show Budget Range — pre-unlock",
    hint: "Visible to guests on the design preview.",
  },
  {
    key: "show_emi_before_unlock",
    label: "Show EMI Range — pre-unlock",
    hint: "Visible to guests on the design preview.",
  },
  {
    key: "show_detailed_budget_after_unlock",
    label: "Show Detailed Budget — post-unlock",
    hint: "Stage-wise breakdown on customer dashboard + PDF.",
  },
  {
    key: "show_detailed_emi_after_unlock",
    label: "Show Detailed EMI — post-unlock",
    hint: "Full EMI schedule on customer dashboard + PDF.",
  },
];

const FlagRow = ({ checked, onChange, label, hint, testId }) => (
  <label
    className="flex items-start gap-3 p-3 rounded-xl border border-border bg-white hover:border-gold/40 cursor-pointer transition-colors"
    data-testid={testId}
  >
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="shrink-0 mt-0.5"
      aria-pressed={checked}
      data-testid={`${testId}-toggle`}
    >
      {checked ? (
        <ToggleRight className="h-7 w-7 text-emerald-600" />
      ) : (
        <ToggleLeft className="h-7 w-7 text-muted-foreground" />
      )}
    </button>
    <div className="min-w-0">
      <div className="text-[13.5px] font-bold text-navy">{label}</div>
      <div className="text-[11.5px] text-muted-foreground leading-snug">
        {hint}
      </div>
    </div>
  </label>
);

const NumField = ({ label, value, onChange, suffix, min, max, step, testId }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
      {label}
    </span>
    <div className="mt-1 relative">
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        min={min}
        max={max}
        step={step}
        data-testid={testId}
        className="w-full min-h-[44px] rounded-xl border-2 border-border bg-white px-3 pr-12 font-bold text-navy text-[14px] focus:border-gold focus:outline-none tabular-nums"
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] font-bold text-muted-foreground pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  </label>
);

const StageSlider = ({ stage, idx, onChange, total, testId }) => {
  const pct = Number(stage.pct || 0);
  return (
    <div
      className="rounded-xl border border-border bg-white px-3 py-2.5"
      data-testid={testId}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[13px] font-bold text-navy truncate">
          {stage.label}
        </span>
        <span
          className="text-[13px] font-extrabold text-gold-dark tabular-nums shrink-0"
          data-testid={`${testId}-value`}
        >
          {pct.toFixed(1)}%
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={0.5}
        value={pct}
        onChange={(e) => onChange(idx, Number(e.target.value))}
        data-testid={`${testId}-slider`}
        className="w-full mt-1.5 accent-gold-dark"
      />
      <div className="text-[10.5px] text-muted-foreground mt-0.5 tabular-nums">
        Total: <strong className={total === 100 ? "text-emerald-600" : "text-navy"}>{total.toFixed(1)}%</strong>
        {total !== 100 && (
          <span className="text-amber-700"> · will auto-normalise on save</span>
        )}
      </div>
    </div>
  );
};

const DEFAULT_STAGES = [
  { key: "foundation", label: "Foundation & Excavation", pct: 15 },
  { key: "rcc", label: "RCC & Structure", pct: 25 },
  { key: "brickwork", label: "Brickwork & Masonry", pct: 15 },
  { key: "plaster_flooring", label: "Plaster & Flooring", pct: 20 },
  { key: "mep", label: "Electrical & Plumbing", pct: 15 },
  { key: "paint_finishing", label: "Paint & Finishing", pct: 10 },
];

const BudgetEMISettingsPanel = ({ onToast }) => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = React.useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    adminGet("/admin/budget/config").then((res) => {
      if (cancelled) return;
      if (res.ok) {
        const data = res.data || {};
        setCfg({
          enable_budget_estimator: !!data.enable_budget_estimator,
          enable_emi_estimator: !!data.enable_emi_estimator,
          show_budget_before_unlock: !!data.show_budget_before_unlock,
          show_emi_before_unlock: !!data.show_emi_before_unlock,
          show_detailed_budget_after_unlock: !!data.show_detailed_budget_after_unlock,
          show_detailed_emi_after_unlock: !!data.show_detailed_emi_after_unlock,
          rate_per_sqft_default: Number(data.rate_per_sqft_default) || 1650,
          rate_per_sqft_min: Number(data.rate_per_sqft_min) || 1500,
          rate_per_sqft_max: Number(data.rate_per_sqft_max) || 1800,
          emi_interest_rate: Number(data.emi_interest_rate) || 8.5,
          emi_loan_pct: Number(data.emi_loan_pct) || 80,
          emi_tenure_years: Number(data.emi_tenure_years) || 20,
          stages:
            Array.isArray(data.stages) && data.stages.length > 0
              ? data.stages.map((s) => ({ ...s, pct: Number(s.pct) || 0 }))
              : DEFAULT_STAGES,
        });
      } else {
        setLoadError({ status: res.status, message: res.error });
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const cancel = load();
    return cancel;
  }, [load]);

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const onStageChange = (idx, value) =>
    setCfg((c) => {
      const next = [...c.stages];
      next[idx] = { ...next[idx], pct: value };
      return { ...c, stages: next };
    });

  const stageTotal = cfg
    ? cfg.stages.reduce((s, x) => s + Number(x.pct || 0), 0)
    : 0;

  const resetStages = () => setCfg((c) => ({ ...c, stages: DEFAULT_STAGES }));

  const save = async () => {
    if (!cfg) return;
    // Auto-normalise stage percentages so they sum to exactly 100.
    let stages = cfg.stages.map((s) => ({
      ...s,
      pct: Math.max(0, Number(s.pct) || 0),
    }));
    const sum = stages.reduce((acc, s) => acc + s.pct, 0);
    if (sum <= 0) {
      onToast?.("Stage percentages must sum to a positive value", "error");
      return;
    }
    if (Math.abs(sum - 100) > 0.001) {
      const factor = 100 / sum;
      stages = stages.map((s) => ({ ...s, pct: +(s.pct * factor).toFixed(2) }));
      // Tiny rounding drift — push it onto the last stage.
      const drift = +(100 - stages.reduce((a, s) => a + s.pct, 0)).toFixed(2);
      if (drift !== 0) {
        stages[stages.length - 1] = {
          ...stages[stages.length - 1],
          pct: +(stages[stages.length - 1].pct + drift).toFixed(2),
        };
      }
    }
    if (cfg.rate_per_sqft_min > cfg.rate_per_sqft_default) {
      onToast?.("Min rate must be ≤ default rate", "error");
      return;
    }
    if (cfg.rate_per_sqft_default > cfg.rate_per_sqft_max) {
      onToast?.("Default rate must be ≤ max rate", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = { ...cfg, stages };
      const res = await adminPut("/admin/budget/config", payload);
      if (!res.ok) {
        onToast?.(res.error || "Save failed — please retry", "error");
        return;
      }
      const data = res.data || {};
      setCfg({
        ...cfg,
        ...data,
        stages: (data.stages || stages).map((s) => ({ ...s, pct: Number(s.pct) || 0 })),
      });
      onToast?.("Budget & EMI settings saved");
    } finally {
      setSaving(false);
    }
  };

  if (loadError && !cfg) {
    return (
      <section
        className="mb-8 rounded-3xl bg-amber-50 border-2 border-amber-300 p-6"
        data-testid="admin-budget-emi-error"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-extrabold text-amber-900">
              Budget &amp; EMI settings failed to load
            </h3>
            <p className="text-[13px] text-amber-900/90 mt-1">
              {loadError.message}{" "}
              {loadError.status ? <span className="font-mono text-[11px]">(HTTP {loadError.status})</span> : null}
            </p>
            <button
              onClick={load}
              data-testid="admin-budget-emi-retry"
              className="mt-3 min-h-[40px] px-3 rounded-xl bg-navy text-gold font-extrabold text-[12.5px] inline-flex items-center gap-1.5 hover:bg-navy-deep"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (loading || !cfg) {
    return (
      <section
        className="mb-8 rounded-2xl bg-white border border-border p-6 text-center text-muted-foreground"
        data-testid="admin-budget-emi-loading"
      >
        Loading Budget & EMI estimator settings…
      </section>
    );
  }

  return (
    <section
      className="mb-8 rounded-3xl bg-gradient-to-br from-white via-white to-sand-alt/40 border-2 border-gold/40 shadow-[0_24px_48px_-24px_rgba(11,37,69,0.25)] overflow-hidden"
      data-testid="admin-budget-emi-panel"
    >
      <header className="bg-navy text-white p-5 flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-gold text-navy grid place-items-center shrink-0 shadow-md">
          <IndianRupee className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-gold font-extrabold">
            Tentative Construction Budget & EMI Estimator
          </div>
          <div className="text-[15px] font-extrabold tracking-tight">
            Live Pricing · Stage breakdown · Home-loan EMI
          </div>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-budget-save-btn"
          className="min-h-[42px] px-4 rounded-xl bg-gold text-navy font-extrabold text-[13.5px] inline-flex items-center gap-1.5 hover:bg-gold-dark hover:text-white disabled:opacity-60"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </header>

      <div className="p-5 sm:p-6 space-y-6">
        {/* Feature flags */}
        <div>
          <div className="text-[12px] uppercase tracking-[0.18em] text-gold-dark font-extrabold mb-2.5">
            Feature Flags · Instant On/Off
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {FLAG_FIELDS.map((f) => (
              <FlagRow
                key={f.key}
                checked={cfg[f.key]}
                onChange={(v) => setField(f.key, v)}
                label={f.label}
                hint={f.hint}
                testId={`admin-budget-flag-${f.key}`}
              />
            ))}
          </div>
        </div>

        {/* Construction rate */}
        <div>
          <div className="text-[12px] uppercase tracking-[0.18em] text-gold-dark font-extrabold mb-2.5">
            Construction Rate (₹ per sqft)
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <NumField
              label="Minimum"
              value={cfg.rate_per_sqft_min}
              onChange={(v) => setField("rate_per_sqft_min", v)}
              suffix="₹/sqft"
              min={500}
              max={10000}
              step={50}
              testId="admin-budget-rate-min"
            />
            <NumField
              label="Default (mid)"
              value={cfg.rate_per_sqft_default}
              onChange={(v) => setField("rate_per_sqft_default", v)}
              suffix="₹/sqft"
              min={500}
              max={10000}
              step={50}
              testId="admin-budget-rate-default"
            />
            <NumField
              label="Maximum"
              value={cfg.rate_per_sqft_max}
              onChange={(v) => setField("rate_per_sqft_max", v)}
              suffix="₹/sqft"
              min={500}
              max={10000}
              step={50}
              testId="admin-budget-rate-max"
            />
          </div>
        </div>

        {/* EMI parameters */}
        <div>
          <div className="text-[12px] uppercase tracking-[0.18em] text-gold-dark font-extrabold mb-2.5 inline-flex items-center gap-2">
            <Wallet className="h-3.5 w-3.5" /> Home-loan EMI
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <NumField
              label="Interest rate (p.a.)"
              value={cfg.emi_interest_rate}
              onChange={(v) => setField("emi_interest_rate", v)}
              suffix="%"
              min={0}
              max={30}
              step={0.1}
              testId="admin-budget-interest-rate"
            />
            <NumField
              label="Loan covers"
              value={cfg.emi_loan_pct}
              onChange={(v) => setField("emi_loan_pct", v)}
              suffix="% of budget"
              min={0}
              max={100}
              step={1}
              testId="admin-budget-loan-pct"
            />
            <NumField
              label="Tenure"
              value={cfg.emi_tenure_years}
              onChange={(v) => setField("emi_tenure_years", v)}
              suffix="years"
              min={1}
              max={40}
              step={1}
              testId="admin-budget-tenure"
            />
          </div>
        </div>

        {/* Stage breakdown */}
        <div>
          <div className="flex items-center justify-between mb-2.5">
            <div className="text-[12px] uppercase tracking-[0.18em] text-gold-dark font-extrabold">
              Stage-wise Cost Allocation
            </div>
            <button
              type="button"
              onClick={resetStages}
              data-testid="admin-budget-reset-stages"
              className="text-[11px] font-bold text-navy/70 hover:text-navy inline-flex items-center gap-1"
            >
              <RotateCcw className="h-3 w-3" /> Reset defaults
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {cfg.stages.map((s, i) => (
              <StageSlider
                key={s.key || i}
                stage={s}
                idx={i}
                onChange={onStageChange}
                total={stageTotal}
                testId={`admin-budget-stage-${s.key || i}`}
              />
            ))}
          </div>
          <div className="mt-2 text-[11.5px] text-muted-foreground inline-flex items-start gap-1.5 leading-snug">
            <Info className="h-3 w-3 mt-0.5 shrink-0" />
            Percentages auto-normalise to 100% on save. The total badge above
            each row stays informational.
          </div>
        </div>
      </div>
    </section>
  );
};

export default BudgetEMISettingsPanel;
